import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { getSongs } from '../../../services/songService';
import MusicalService from '../../../services/musicalService';
import TalentService from '../../../services/talentService';
import VideoService from '../../../services/videoService';

export const RecentItems = ({ userRole, isEditor, isSuperUser }) => {
  const navigate = useNavigate();
  const [recentItems, setRecentItems] = useState({
    song: null,
    musical: null,
    talent: null,
    video: null
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecentItems();
  }, []);

  const loadRecentItems = async () => {
    setLoading(true);
    try {
      const [songsResponse, musicalsResponse, talentResponse, videosResponse] = await Promise.all([
        // Get most recent song from songs table
        getSongs({ limit: 1 })?.catch(() => ({ data: [] })),
        // Get most recent musical
        MusicalService?.getMusicals?.()?.catch(() => ({ data: [] })),
        // Get most recent talent (only for superusers)
        isSuperUser ? TalentService?.getAllTalentProfiles?.()?.catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        // Get most recent video (only for superusers)  
        isSuperUser ? VideoService?.getAllVideos?.()?.catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ]);

      const newRecentItems = {
        song: songsResponse?.data?.[0] || null,
        musical: musicalsResponse?.data?.[0] || null,
        talent: isSuperUser ? talentResponse?.data?.[0] || null : null,
        video: isSuperUser ? videosResponse?.data?.[0] || null : null
      };

      setRecentItems(newRecentItems);
    } catch (error) {
      console.error('Error loading recent items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (type, item) => {
    if (!item) {
      // If no item exists, navigate to add forms for editors/superusers, otherwise to list views
      switch (type) {
        case 'song':
          if (isEditor || isSuperUser) {
            navigate('/song-addition-form');
          } else {
            navigate('/song-database-list');
          }
          break;
        case 'musical':
          if (isEditor || isSuperUser) {
            navigate('/add-musical');
          } else {
            navigate('/all-musicals');
          }
          break;
        case 'talent':
          if (isSuperUser) {
            navigate('/add-edit-talent-profile');
          }
          break;
        case 'video':
          if (isSuperUser) {
            navigate('/all-videos-management');
          }
          break;
        default:
          break;
      }
      return;
    }

    // Navigate to detail pages for existing items
    switch (type) {
      case 'song':
        // Always navigate to song detail page
        navigate(`/song-detail-page?id=${item?.id}`);
        break;
      case 'musical':
        // Navigate to musical detail page in add-musical (acts as detail/edit page)
        navigate(`/add-musical?id=${item?.id}`);
        break;
      case 'talent':
        // Navigate to talent edit page
        if (item?.id) {
          navigate(`/add-edit-talent-profile/${item?.id}`);
        } else {
          navigate('/talent-management-dashboard');
        }
        break;
      case 'video':
        // Navigate to video details within the video management page
        navigate(`/all-videos-management?video=${item?.id}`);
        break;
      default:
        break;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getItemTitle = (type, item) => {
    if (!item) return `No ${type} found`;
    
    switch (type) {
      case 'song':
        return item?.title || 'Untitled Song';
      case 'musical':
        return item?.title || 'Untitled Musical';
      case 'talent':
        return item?.display_name || `${item?.first_name || ''} ${item?.last_name || ''}`?.trim() || 'Unnamed Talent';
      case 'video':
        return item?.title || item?.file_name || 'Untitled Video';
      default:
        return 'Unknown';
    }
  };

  const getItemSubtitle = (type, item) => {
    if (!item) return `Add your first ${type}`;
    
    switch (type) {
      case 'song':
        return item?.musical?.title || 'From unknown musical';
      case 'musical':
        return item?.composer || 'Unknown composer';
      case 'talent':
        return item?.email || 'No contact info';
      case 'video':
        return `${item?.duration_seconds || 0}s duration`;
      default:
        return '';
    }
  };

  const getItemIcon = (type) => {
    switch (type) {
      case 'song': return 'Music';
      case 'musical': return 'Theater';
      case 'talent': return 'Star';
      case 'video': return 'Video';
      default: return 'FileText';
    }
  };

  const getGradient = (type) => {
    switch (type) {
      case 'song': return 'from-blue-500 to-cyan-500';
      case 'musical': return 'from-purple-500 to-pink-500';
      case 'talent': return 'from-green-500 to-teal-500';
      case 'video': return 'from-orange-500 to-red-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const items = [
    { type: 'song', label: 'Most Recent Song Added', available: true },
    { type: 'musical', label: 'Most Recent Musical Added', available: true },
    { type: 'talent', label: 'Most Recent Talent Added', available: isSuperUser },
    { type: 'video', label: 'Most Recent Video Added', available: isSuperUser }
  ];

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4]?.map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-20 bg-white bg-opacity-10 rounded-xl"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {items?.filter(item => item?.available)?.map(({ type, label }) => {
        const item = recentItems?.[type];
        const hasItem = !!item;
        
        return (
          <button
            key={type}
            onClick={() => handleItemClick(type, item)}
            className="w-full p-4 bg-white bg-opacity-5 hover:bg-opacity-10 border border-white border-opacity-10 hover:border-opacity-20 rounded-xl transition-all duration-200 group"
          >
            <div className="flex items-center space-x-4">
              {/* Icon */}
              <div className={`p-3 rounded-lg bg-gradient-to-r ${getGradient(type)} flex-shrink-0`}>
                <Icon name={getItemIcon(type)} size={20} className="text-white" />
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0 text-left">
                <h4 className="text-white font-medium text-sm mb-1">
                  {label}
                </h4>
                {hasItem ? (
                  <>
                    <p className="text-blue-100 font-semibold truncate">
                      {getItemTitle(type, item)}
                    </p>
                    <p className="text-blue-200 text-xs opacity-80 truncate">
                      {getItemSubtitle(type, item)}
                    </p>
                    <p className="text-blue-300 text-xs opacity-60 mt-1">
                      Added {formatDate(item?.created_at)}
                    </p>
                  </>
                ) : (
                  <p className="text-blue-200 text-sm opacity-70">
                    No {type} found - Click to add one
                  </p>
                )}
              </div>
              
              {/* Arrow */}
              <Icon 
                name="ArrowRight" 
                size={16} 
                className="text-blue-200 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200 flex-shrink-0" 
              />
            </div>
          </button>
        );
      })}
    </div>
  );
};